import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as AddIcon
} from '@mui/icons-material';
import api from '../../../services/api';

function AddMembersTab({ serviceGroup, token, onRefresh, currentMembers }) {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Group role to assign. Empty string = "auto-detect from User.role" (legacy
  // behaviour: maps clinical_supervisor→supervisor, therapist→therapist,
  // admin→coordinator, otherwise→client).
  const isTrainingOrg = serviceGroup.groupType === 'educational_institution' || serviceGroup.groupType === 'training_program' || serviceGroup.trainingConfig?.enabled;
  const [groupRoleOverride, setGroupRoleOverride] = useState(isTrainingOrg ? 'student' : '');

  // Filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadAvailableUsers();
  }, [page, rowsPerPage, roleFilter, searchQuery, token, currentMembers]);

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);

      // Get all users using the correct admin endpoint
      const response = await api.get('/admin/users');
      let users = response.data || [];

      // Filter out current members
      const currentMemberIds = currentMembers.map(m => m._id);
      users = users.filter(u => !currentMemberIds.includes(u._id));

      // Apply role filter
      if (roleFilter !== 'all') {
        users = users.filter(u => {
          const userRoles = Array.isArray(u.role) ? u.role : [u.role];
          return userRoles.includes(roleFilter);
        });
      }

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        users = users.filter(u =>
          u.name?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query)
        );
      }

      // Apply pagination
      setTotalCount(users.length);
      const start = page * rowsPerPage;
      const paginatedUsers = users.slice(start, start + rowsPerPage);

      setAvailableUsers(paginatedUsers);
      setError('');
    } catch (err) {
      console.error('Error loading available users:', err);
      setError(err.response?.data?.msg || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Add each selected user to the group
      for (const userId of selectedUsers) {
        const user = availableUsers.find(u => u._id === userId);

        // Determine role in service group
        let groupRole = 'client'; // default
        if (groupRoleOverride) {
          groupRole = groupRoleOverride;
        } else {
          const userRoles = Array.isArray(user.role) ? user.role : [user.role];
          if (userRoles.includes('clinical_supervisor')) {
            groupRole = 'supervisor';
          } else if (userRoles.includes('therapist')) {
            groupRole = 'therapist';
          } else if (userRoles.includes('admin')) {
            groupRole = 'coordinator';
          }
        }

        await api.post(`/service-groups/${serviceGroup._id}/members`, {
          userId,
          role: groupRole,
          notes: `Added as ${groupRole} via member management on ${new Date().toLocaleDateString()}`
        });
      }

      setSuccess(`Successfully added ${selectedUsers.length} member(s) to the group`);
      setSelectedUsers([]);
      await loadAvailableUsers();
      onRefresh();
    } catch (err) {
      console.error('Error adding members:', err);
      setError(err.response?.data?.msg || 'Failed to add members');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(availableUsers.map(u => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleToggleUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const getUserDisplayRole = (user) => {
    const roles = Array.isArray(user.role) ? user.role : [user.role];
    if (roles.includes('clinical_supervisor')) return 'Clinical Supervisor';
    if (roles.includes('therapist')) return 'Therapist';
    if (roles.includes('lecturer')) return 'Lecturer';
    if (roles.includes('student')) return 'Student';
    if (roles.includes('admin')) return 'Admin';
    return 'Client';
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={roleFilter}
            label="Filter by Role"
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="client">Clients</MenuItem>
            <MenuItem value="therapist">Therapists</MenuItem>
            <MenuItem value="clinical_supervisor">Clinical Supervisors</MenuItem>
            <MenuItem value="lecturer">Lecturers</MenuItem>
            <MenuItem value="student">Students</MenuItem>
            <MenuItem value="admin">Admins</MenuItem>
          </Select>
        </FormControl>

        {/* Role to assign in this service group. Available options depend on
            whether this is a training org or a clinical service group. */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Assign as Role</InputLabel>
          <Select
            value={groupRoleOverride}
            label="Assign as Role"
            onChange={(e) => setGroupRoleOverride(e.target.value)}
          >
            {!isTrainingOrg && <MenuItem value="">Auto-detect from user role</MenuItem>}
            {isTrainingOrg ? [
              <MenuItem key="training_admin" value="training_admin">Training Admin</MenuItem>,
              <MenuItem key="lecturer" value="lecturer">Lecturer</MenuItem>,
              <MenuItem key="student" value="student">Student</MenuItem>,
            ] : [
              <MenuItem key="client" value="client">Client</MenuItem>,
              <MenuItem key="therapist" value="therapist">Therapist</MenuItem>,
              <MenuItem key="supervisor" value="supervisor">Clinical Supervisor</MenuItem>,
              <MenuItem key="coordinator" value="coordinator">Coordinator</MenuItem>,
            ]}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          label="Search users"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 500, mb: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < availableUsers.length}
                  checked={availableUsers.length > 0 && selectedUsers.length === availableUsers.length}
                  onChange={handleSelectAll}
                  disabled={availableUsers.length === 0}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : availableUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No available users to add
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              availableUsers.map((user) => (
                <TableRow
                  key={user._id}
                  hover
                  onClick={() => handleToggleUser(user._id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={selectedUsers.includes(user._id)} />
                  </TableCell>
                  <TableCell>{user.name || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={getUserDisplayRole(user)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status || 'Unknown'}
                      size="small"
                      color={user.status === 'Active' ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleBulkAdd}
          disabled={selectedUsers.length === 0 || loading}
        >
          Add {selectedUsers.length > 0 ? `${selectedUsers.length} ` : ''}Selected Member{selectedUsers.length !== 1 ? 's' : ''}
        </Button>
      </Box>
    </Box>
  );
}

export default AddMembersTab;
